import cn from 'classnames';
import { find, upperCase, isNil } from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';

import DeleteItemPopconfirmIcon from '@/comp/pages/EnginePage/Engine/Common/DeleteItemPopconfirmIcon/DeleteItemPopconfirmIcon';
import TableHeaderCell from '@/comp/pages/EnginePage/Engine/Common/TableHeaderCell/TableHeaderCell';
import ValidationTooltip from '@/comp/pages/EnginePage/Engine/Common/ValidationTooltip/ValidationTooltip';
import { EngineSubstepsEnum, EngineStepsEnum } from '@/store/Engine/constants';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { maxLength, onlyDigits } from '@/utils/util-functions';

import { Input } from './styled';
import { LONG_DASH } from '../../EngineVersionUtils';

const FlowLevelsTable = observer(
  ({
    hasTriedToSubmit,
    selectedSubstepName,
  }: {
    hasTriedToSubmit?: boolean;
    selectedSubstepName?: EngineSubstepsEnum;
  }) => {
    const { t } = useTranslation();
    const { tunit } = useMeasurement();
    const { engineStore } = useStores();
    const {
      mainRangesConfiguration: { flowLevels, unit },
      validationMessages,
    } = engineStore;

    const onRangeFieldChange =
      (key, field) =>
      ({ target: { value } }) => {
        flowLevels.setRangeField(key, field, value);
        if (hasTriedToSubmit)
          engineStore.validate(
            EngineStepsEnum.MainRangesConfiguration,
            EngineSubstepsEnum.FlowLevels,
            `${field}-${key}`,
          );
      };

    const onDeleteRangeClick = key => () => flowLevels.deleteRange(key);

    const onUpperBoundKeyPress = event => onlyDigits(event);

    const columns = [
      {
        dataIndex: 'name',
        key: 'name',
        title: <TableHeaderCell titleKey="name" className="header-cell" />,
        width: '24%',
        render: name => (
          <span
            className={cn('range-name', selectedSubstepName === EngineSubstepsEnum.MainRangesResult && 'read-only')}
          >
            {name}
          </span>
        ),
      },
      {
        dataIndex: 'lowerBound',
        key: 'lowerBound',
        title: <TableHeaderCell titleKey="lowerBoundFlow" className="header-cell" />,
        width: '38%',
        render: (lowerBound, { key }) => (
          <div className="input-flex-row">
            <div
              className={cn(
                'value-unit',
                engineStore.hasFinishedWizard &&
                  (isNil(lowerBound) || lowerBound !== flowLevels.getOldRangeValues(key)?.lowerBound) &&
                  'edited',
              )}
            >
              <Input
                value={lowerBound}
                onChange={onRangeFieldChange(key, 'lowerBound')}
                {...(!engineStore.hasFinishedWizard && { placeholder: LONG_DASH })}
                bordered={false}
                disabled
                $hasBeenEdited={
                  engineStore.hasFinishedWizard &&
                  (isNil(lowerBound) || lowerBound !== flowLevels.getOldRangeValues(key)?.lowerBound)
                }
                $valueLength={`${lowerBound ?? ''}`?.length}
              />
              {lowerBound ? <span className="input-unit">{tunit('m3/min', upperCase(unit))}</span> : null}
            </div>
          </div>
        ),
      },
      {
        dataIndex: 'upperBound',
        key: 'upperBound',
        title: <TableHeaderCell titleKey="upperBoundFlow" className="header-cell" />,
        width: '38%',
        render: (upperBound, { key, name }, index) => {
          const message = find(validationMessages, { key, field: 'upperBound', focused: true })?.message;
          return (
            <div className="upper-bound">
              <div className="input-flex-row">
                <div
                  className={cn(
                    'value-unit',
                    engineStore.hasFinishedWizard &&
                      (isNil(upperBound) || upperBound !== flowLevels.getOldRangeValues(key)?.upperBound) &&
                      'edited',
                  )}
                >
                  <ValidationTooltip message={hasTriedToSubmit && message} tArgs={{ ranges: 'flowRanges' }}>
                    <Input
                      value={upperBound}
                      onChange={onRangeFieldChange(key, 'upperBound')}
                      {...(!engineStore.hasFinishedWizard && { placeholder: t('addUpperRange') })}
                      bordered={false}
                      count={maxLength(4)}
                      $shouldUnderline
                      disabled={
                        upperBound === Infinity ||
                        engineStore.isReadOnly ||
                        selectedSubstepName === EngineSubstepsEnum.MainRangesResult
                      }
                      onKeyPress={onUpperBoundKeyPress}
                      $hasBeenEdited={
                        engineStore.hasFinishedWizard &&
                        (isNil(upperBound) || upperBound !== flowLevels.getOldRangeValues(key)?.upperBound)
                      }
                      $valueLength={`${upperBound ?? ''}`?.length}
                      $isEditable
                    />
                  </ValidationTooltip>
                  {upperBound && upperBound !== Infinity ? (
                    <span
                      className={cn(
                        'input-unit',
                        !engineStore.isReadOnly &&
                          selectedSubstepName !== EngineSubstepsEnum.MainRangesResult &&
                          'editable',
                      )}
                    >
                      {tunit('m3/min', upperCase(unit))}
                    </span>
                  ) : null}
                </div>
              </div>
              {index > 0 && !engineStore.isReadOnly && selectedSubstepName !== EngineSubstepsEnum.MainRangesResult && (
                <DeleteItemPopconfirmIcon itemName={name} onConfirm={onDeleteRangeClick(key)} />
              )}
            </div>
          );
        },
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={flowLevels.ranges}
        pagination={false}
        {...(flowLevels.ranges?.length > 4 &&
          selectedSubstepName !== EngineSubstepsEnum.MainRangesResult && {
            scroll: { y: 204 },
          })}
        className="table"
      />
    );
  },
);

export default FlowLevelsTable;
